// File: StudentExit.js — จำหน่ายนักเรียน / สำเร็จการศึกษา (งานทะเบียน pos 8, 9, 99)
//
// ทั้งสองกรณีคือการออกจากทะเบียนเหมือนกัน (st_status = '0') ต่างกันที่สาเหตุ ซึ่งเก็บ
// ไว้ใน tb_student_exit พร้อมวันที่และปลายทางที่ไปเรียนต่อ — ดู migration
// 20260914_create_student_exit.sql สำหรับเหตุผลที่ไม่แยกเป็นสถานะคนละตัว
import React, { useCallback, useEffect, useRef, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Badge, Button, Card, Col, Form, InputGroup, Modal, Row, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from "../contexts/LoadingContext";
import { toThaiDate } from "../admission/fields";
import StudentExitBackfill from "./StudentExitBackfill";
import {
  WITHDRAW_REASONS, TYPE_LABEL, todayISO, toInputDate, reasonToForm,
  classLabel, islamicClassLabel, bothClassesLabel, suggestedType, statusBadge,
  academicYearEndISO,
} from "./studentExitShared";

const MODE_TITLE = {
  create: 'จำหน่ายนักเรียน',
  edit: 'แก้ไขข้อมูลการจำหน่าย',
  backfill: 'ใส่ข้อมูลการออกย้อนหลัง',
};

const MODE_SAVE = { create: 'บันทึก', edit: 'บันทึกการแก้ไข', backfill: 'บันทึกย้อนหลัง' };

const EMPTY_FORM = {
  ex_type: "withdraw",
  ex_date: todayISO(),
  reasonChoice: "ย้ายสถานศึกษา",
  reasonOther: "",
  ex_destination: "",
  ex_note: "",
};

// สถานะใบคืนหอพักของคนที่ยังค้างเตียงอยู่
//
// server ปฏิเสธการจำหน่ายคนที่ยังมีทะเบียนหอและยังไม่มีใบที่เซ็นผ่าน (dormClearanceModel
// .clearanceGuard) — หน้านี้จึงต้องบอกตั้งแต่ตอนค้นหา ไม่ใช่รอให้กดยืนยันกล่องแดงแล้วค่อย
// ขึ้น error — คนที่ต้องทำงานต่อคือเจ้าหน้าที่หอพัก ไม่ใช่ฝ่ายทะเบียน
const dormBlocked = (row) =>
  String(row.st_status) !== '0' && !!row.dorm_name && row.dc_status !== 'cleared';

const clearanceNote = (row) => {
  if (String(row.st_status) === '0') return null;
  if (row.dc_status === 'cleared') {
    return <div className="text-success">คืนหอแล้ว ({row.dc_no})</div>;
  }
  if (row.dc_status === 'pending') {
    return <div className="text-warning">ใบคืนหอ {row.dc_no} ยังตรวจไม่เสร็จ</div>;
  }
  return <div className="text-danger">ยังไม่คืนหอพัก</div>;
};

export default function StudentExit() {
  ///// Skin Switch /////
  const currentSkin = (localStorage.getItem('skin-mode')) ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);

  const switchSkin = (skin) => {
    if (skin === 'dark') {
      const btnWhite = document.getElementsByClassName('btn-white');
      for (const btn of btnWhite) {
        btn.classList.add('btn-outline-primary');
        btn.classList.remove('btn-white');
      }
    } else {
      const btnOutlinePrimary = document.getElementsByClassName('btn-outline-primary');
      for (const btn of btnOutlinePrimary) {
        btn.classList.remove('btn-outline-primary');
        btn.classList.add('btn-white');
      }
    }
  };

  switchSkin(skin);
  useEffect(() => { switchSkin(skin); }, [skin]);

  const { setLoading } = useLoading();
  const searchRef = useRef(null);

  const [searchKey, setSearchKey] = useState("");
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [recent, setRecent] = useState([]);
  const [leftovers, setLeftovers] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [target, setTarget] = useState(null);
  const [editId, setEditId] = useState(null);   // null = บันทึกใหม่, มีค่า = แก้ ex_id นั้น
  // 'create' = จำหน่ายตอนนี้ (เปลี่ยนสถานะ+คืนเตียง) | 'edit' = แก้ใบเดิม
  // 'backfill' = ใส่ข้อมูลย้อนหลังให้คนที่ออกไปแล้วตั้งแต่ก่อนมีหน้านี้ (ไม่แตะสถานะ/หอพัก)
  const [mode, setMode] = useState('create');
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const loadRecent = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/api/studentExit/recent?limit=20');
      setRecent(res.data?.data || []);
    } catch (err) {
      console.error('load recent exits failed', err);
    }
  }, []);

  // คนที่ถูกจำหน่ายไปแล้วแต่ยังถือเตียงในหอ — แถวที่บันทึกไว้ก่อนที่การจำหน่ายจะคืนเตียงให้เอง
  // และคนที่ฝ่ายหอพักลงทะเบียนที่พักให้หลังจากถูกจำหน่าย ถ้าไม่ขึ้นตรงนี้ก็ไม่มีใครเห็น
  const loadLeftovers = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/api/studentExit/dormLeftovers');
      setLeftovers(res.data?.data || []);
    } catch (err) {
      console.error('load dorm leftovers failed', err);
    }
  }, []);

  useEffect(() => { loadRecent(); }, [loadRecent]);
  useEffect(() => { loadLeftovers(); }, [loadLeftovers]);
  useEffect(() => { searchRef.current?.focus(); }, []);

  const runSearch = async (key) => {
    const q = String(key).trim();
    if (q.length < 2) {
      Swal.fire({ icon: 'info', title: 'กรอกอย่างน้อย 2 ตัวอักษร', text: 'ค้นด้วยเลขประจำตัวหรือชื่อนักเรียน' });
      return;
    }
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/api/studentExit/search?q=${encodeURIComponent(q)}`);
      setResults(res.data?.data || []);
      setSearched(true);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'ค้นหาไม่สำเร็จ', text: err.response?.data?.message || 'เกิดข้อผิดพลาด' });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    runSearch(searchKey);
  };

  const openForm = (row) => {
    setTarget(row);
    setEditId(null);
    setMode('create');
    setForm({ ...EMPTY_FORM, ex_date: todayISO(), ex_type: suggestedType(row) });
    setShowForm(true);
  };

  // แก้รายการที่บันทึกไปแล้ว — รับได้ทั้งแถวจากตารางผลค้นหา (ข้อมูลการจำหน่ายครั้งล่าสุด
  // ติดมากับแถวนักเรียน) และแถวจากตารางรายการล่าสุด ซึ่งมีคนละชุดฟิลด์กัน
  const openEdit = (row) => {
    setTarget(row);
    setEditId(row.ex_id);
    setMode('edit');
    setForm({
      ex_type: row.ex_type || 'withdraw',
      ex_date: toInputDate(row.ex_date) || todayISO(),
      ...reasonToForm(row.ex_reason),
      ex_destination: row.ex_destination || '',
      ex_note: row.ex_note || '',
    });
    setShowForm(true);
  };

  // ใส่ข้อมูลย้อนหลังให้ทีละคนจากผลค้นหา — ตั้งวันที่เริ่มต้นเป็นวันสิ้นปีการศึกษาที่เขา
  // มีทะเบียนเป็นปีสุดท้าย ซึ่งใกล้ความจริงกว่าวันนี้เสมอ แล้วให้ทะเบียนแก้เป็นวันจริงถ้ามีเอกสาร
  const openBackfill = (row) => {
    setTarget(row);
    setEditId(null);
    setMode('backfill');
    setForm({
      ...EMPTY_FORM,
      ex_type: suggestedType(row),
      ex_date: academicYearEndISO(row.gen_year) || todayISO(),
    });
    setShowForm(true);
  };

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const handleSave = async () => {
    if (!target) return;
    if (!form.ex_date) {
      Swal.fire({ icon: 'warning', title: 'ยังไม่ได้ระบุวันที่' });
      return;
    }
    const reason = form.ex_type === 'withdraw'
      ? (form.reasonChoice === 'อื่นๆ' ? form.reasonOther.trim() : form.reasonChoice)
      : null;
    if (form.ex_type === 'withdraw' && !reason && mode !== 'backfill') {
      Swal.fire({ icon: 'warning', title: 'ยังไม่ได้ระบุสาเหตุการจำหน่าย' });
      return;
    }

    // การแก้ไม่ได้ตัดใครออกจากทะเบียนเพิ่ม จึงไม่ต้องผ่านกล่องยืนยันแบบเดียวกับการจำหน่าย
    if (editId) {
      setSaving(true);
      try {
        const res = await axiosInstance.put(`/api/studentExit/${editId}`, {
          ex_type: form.ex_type,
          ex_date: form.ex_date,
          ex_reason: reason,
          ex_destination: form.ex_destination.trim() || null,
          ex_note: form.ex_note.trim() || null,
        });
        setShowForm(false);
        Swal.fire({ icon: 'success', title: res.data?.message || 'แก้ไขแล้ว', timer: 2200, showConfirmButton: false });
        await Promise.all([searched ? runSearch(searchKey) : Promise.resolve(), loadRecent(), loadLeftovers()]);
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'แก้ไขไม่สำเร็จ', text: err.response?.data?.message || 'เกิดข้อผิดพลาด' });
      } finally {
        setSaving(false);
      }
      return;
    }

    // ใส่ย้อนหลังไม่ได้ตัดใครออกจากทะเบียนเพิ่ม (เขาออกไปแล้ว) จึงไม่ต้องมีกล่องเตือนสีแดง
    if (mode === 'backfill') {
      setSaving(true);
      try {
        const res = await axiosInstance.post('/api/studentExit/backfill', {
          student_ids: [target.st_id],
          ex_type: form.ex_type,
          ex_date: form.ex_date,
          ex_reason: reason,
          ex_destination: form.ex_destination.trim() || null,
          ex_note: form.ex_note.trim() || null,
        });
        setShowForm(false);
        Swal.fire({ icon: 'success', title: res.data?.message || 'บันทึกแล้ว', timer: 2600, showConfirmButton: false });
        await Promise.all([runSearch(searchKey), loadLeftovers()]);
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'บันทึกไม่สำเร็จ', text: err.response?.data?.message || 'เกิดข้อผิดพลาด' });
      } finally {
        setSaving(false);
      }
      return;
    }

    const confirm = await Swal.fire({
      icon: 'warning',
      title: form.ex_type === 'graduate' ? 'ยืนยันสำเร็จการศึกษา' : 'ยืนยันการจำหน่าย',
      html: `<div class="text-start">
        <div><b>${target.st_name}</b> (${target.st_idnumber})</div>
        <div>ชั้นล่าสุด ${bothClassesLabel(target)}</div>
        <div>วันที่ ${toThaiDate(form.ex_date) || form.ex_date}</div>
        ${reason ? `<div>สาเหตุ ${reason}</div>` : ''}
        ${form.ex_destination.trim() ? `<div>ไปเรียนต่อ ${form.ex_destination.trim()}</div>` : ''}
        ${target.dc_no ? `<div>คืนหอพักแล้วตามใบเลขที่ ${target.dc_no}</div>` : ''}
        ${target.dorm_name ? `<div>พักที่หอ ${target.dorm_name} — จะย้ายออกจากหอและคืนเตียงให้ด้วย</div>` : ''}
      </div><div class="mt-2 text-danger">นักเรียนจะถูกตัดออกจากรายชื่อห้องเรียน การเช็คชื่อ และรายงานทั้งหมด</div>`,
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#d33',
    });
    if (!confirm.isConfirmed) return;

    setSaving(true);
    try {
      const res = await axiosInstance.post('/api/studentExit', {
        student_id: target.st_id,
        ex_type: form.ex_type,
        ex_date: form.ex_date,
        ex_reason: reason,
        ex_destination: form.ex_destination.trim() || null,
        ex_note: form.ex_note.trim() || null,
      });
      setShowForm(false);
      Swal.fire({ icon: 'success', title: res.data?.message || 'บันทึกแล้ว', timer: 2200, showConfirmButton: false });
      await Promise.all([runSearch(searchKey), loadRecent(), loadLeftovers()]);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'บันทึกไม่สำเร็จ', text: err.response?.data?.message || 'เกิดข้อผิดพลาด' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelExit = async (row) => {
    const confirm = await Swal.fire({
      icon: 'question',
      title: 'ยกเลิกการจำหน่าย?',
      html: `<div><b>${row.st_name}</b> (${row.st_idnumber})</div>
             <div class="mt-1">สถานะจะกลับไปเป็นนักเรียนปัจจุบัน และรายการจำหน่ายครั้งล่าสุดจะถูกลบ</div>
             <div class="mt-1">ถ้าตอนจำหน่ายมีการย้ายออกจากหอ ระบบจะคืนที่พักเดิมให้ถ้าหลังนั้นยังมีที่ว่าง</div>`,
      showCancelButton: true,
      confirmButtonText: 'ยกเลิกการจำหน่าย',
      cancelButtonText: 'ปิด',
    });
    if (!confirm.isConfirmed) return;
    setLoading(true);
    try {
      const res = await axiosInstance.post('/api/studentExit/cancel', { student_id: row.st_id });
      Swal.fire({ icon: 'success', title: res.data?.message || 'ยกเลิกแล้ว', timer: 3000, showConfirmButton: false });
      await Promise.all([searched ? runSearch(searchKey) : Promise.resolve(), loadRecent(), loadLeftovers()]);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'ยกเลิกไม่สำเร็จ', text: err.response?.data?.message || 'เกิดข้อผิดพลาด' });
    } finally {
      setLoading(false);
    }
  };

  const handleReleaseDorm = async (row) => {
    const confirm = await Swal.fire({
      icon: 'question',
      title: 'ย้ายออกจากหอและคืนเตียง?',
      html: `<div><b>${row.st_name}</b> (${row.st_idnumber})</div>
             <div class="mt-1">หอ ${row.dorm_name || '-'} ${row.village_name || ''}</div>
             <div class="mt-1">ทะเบียนหอพักจะถูกปิด และเตียงนี้จะกลับไปนับเป็นที่ว่าง</div>`,
      showCancelButton: true,
      confirmButtonText: 'ย้ายออกจากหอ',
      cancelButtonText: 'ปิด',
    });
    if (!confirm.isConfirmed) return;
    setLoading(true);
    try {
      const res = await axiosInstance.post('/api/studentExit/dormRelease', { student_id: row.st_id });
      Swal.fire({ icon: 'success', title: res.data?.message || 'คืนเตียงแล้ว', timer: 2200, showConfirmButton: false });
      await Promise.all([
        searched ? runSearch(searchKey) : Promise.resolve(),
        loadLeftovers(),
      ]);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'คืนที่พักไม่สำเร็จ', text: err.response?.data?.message || 'เกิดข้อผิดพลาด' });
    } finally {
      setLoading(false);
    }
  };

  // หลังใส่ข้อมูลย้อนหลัง แถวเดิมในผลค้นหาและในการ์ดหอพักจะมีใบจำหน่ายแล้ว จึงต้องโหลดใหม่
  // ส่วน "รายการล่าสุด" ไม่ต้อง เพราะโดยตั้งใจแล้วมันไม่รับแถวที่ใส่ย้อนหลัง
  const refreshAfterBackfill = async () => {
    await Promise.all([
      searched ? runSearch(searchKey) : Promise.resolve(),
      loadLeftovers(),
    ]);
  };

  const isWithdraw = form.ex_type === 'withdraw';

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">ทะเบียนและหลักสูตร</Link></li>
              <li className="breadcrumb-item active" aria-current="page">จำหน่ายนักเรียน</li>
            </ol>
            <h4 className="main-title mb-0">จำหน่ายนักเรียน / สำเร็จการศึกษา</h4>
          </div>
        </div>

        <Card className="card-one mb-4">
          <Card.Header>
            <Card.Title as="h6" className="mb-0">ค้นหานักเรียน</Card.Title>
          </Card.Header>
          <Card.Body>
            <Form onSubmit={handleSearch}>
              <Row className="g-2 align-items-center">
                <Col md="6">
                  <InputGroup>
                    <Form.Control
                      ref={searchRef}
                      value={searchKey}
                      onChange={(e) => setSearchKey(e.target.value)}
                      placeholder="เลขประจำตัวนักเรียน หรือชื่อ"
                    />
                    <Button type="submit" variant="primary">
                      <i className="ri-search-line"></i> ค้นหา
                    </Button>
                  </InputGroup>
                </Col>
                <Col md="6">
                  <div className="fs-sm text-secondary">
                    พิมพ์เลขประจำตัวแล้วกด Enter — เลขที่ตรงทั้งหมดจะขึ้นเป็นรายการแรก
                  </div>
                </Col>
              </Row>
            </Form>

            {searched && (
              <div className="table-responsive mt-3">
                <Table className="table-bordered mb-0" hover>
                  <thead className="table-secondary">
                    <tr>
                      <th style={{ width: 130 }} rowSpan={2} className="align-middle">เลขประจำตัว</th>
                      <th rowSpan={2} className="align-middle">ชื่อ - สกุล</th>
                      <th colSpan={2} className="text-center">ชั้นล่าสุด</th>
                      <th style={{ width: 150 }} rowSpan={2} className="align-middle">หอพัก</th>
                      <th style={{ width: 150 }} rowSpan={2} className="align-middle">สถานะ</th>
                      <th style={{ width: 230 }} rowSpan={2} className="align-middle">รายละเอียดการออก</th>
                      <th style={{ width: 210 }} rowSpan={2} className="align-middle text-center">จัดการ</th>
                    </tr>
                    <tr>
                      <th style={{ width: 130 }} className="text-center">สามัญ</th>
                      <th style={{ width: 120 }} className="text-center">ศาสนา</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.length === 0 && (
                      <tr><td colSpan="8" className="text-center text-secondary py-4">ไม่พบนักเรียนตามที่ค้นหา</td></tr>
                    )}
                    {results.map((row) => (
                      <tr key={row.st_id}>
                        <td>{row.st_idnumber}</td>
                        <td>{row.st_name}</td>
                        <td className="text-center">{classLabel(row)}</td>
                        <td className="text-center">{islamicClassLabel(row)}</td>
                        <td className="fs-sm">
                          {row.dorm_name ? (
                            <>
                              <div>{row.dorm_name}</div>
                              {row.village_name && <div className="text-secondary">{row.village_name}</div>}
                              {/* คนที่ยังค้างเตียงจำหน่ายไม่ได้จนกว่าหอพักเซ็นใบคืนหอให้ —
                                  บอกตั้งแต่ตอนค้นหา ไม่ใช่รอให้กดยืนยันแล้วค่อยขึ้น error */}
                              {clearanceNote(row)}
                            </>
                          ) : (
                            <span className="text-secondary">ไม่มีทะเบียนหอพัก</span>
                          )}
                        </td>
                        <td>{statusBadge(row)}</td>
                        <td className="fs-sm">
                          {String(row.st_status) === '0' && row.ex_date ? (
                            <>
                              <div>วันที่ {toThaiDate(row.ex_date)}</div>
                              {row.ex_reason && <div className="text-secondary">{row.ex_reason}</div>}
                              {row.ex_destination && <div className="text-secondary">ไปเรียนต่อ {row.ex_destination}</div>}
                            </>
                          ) : String(row.st_status) === '0' ? (
                            <span className="text-danger fs-sm">ยังไม่มีข้อมูลการออก</span>
                          ) : (
                            <span className="text-secondary">-</span>
                          )}
                        </td>
                        <td className="text-center">
                          {String(row.st_status) === '0' ? (
                            <div className="d-flex flex-column gap-1">
                              {row.ex_id ? (
                                <Button size="sm" variant="outline-primary" onClick={() => openEdit(row)}>
                                  <i className="ri-edit-line"></i> แก้ไขข้อมูลการออก
                                </Button>
                              ) : (
                                <Button size="sm" variant="outline-danger" onClick={() => openBackfill(row)}>
                                  <i className="ri-history-line"></i> ใส่ข้อมูลย้อนหลัง
                                </Button>
                              )}
                              <Button size="sm" variant="outline-secondary" onClick={() => handleCancelExit(row)}>
                                <i className="ri-arrow-go-back-line"></i> ยกเลิกการจำหน่าย
                              </Button>
                            </div>
                          ) : dormBlocked(row) ? (
                            <div className="d-flex flex-column gap-1">
                              <Button size="sm" variant="outline-secondary" disabled>
                                <i className="ri-lock-line"></i> รอหอพักเซ็นใบคืนหอ
                              </Button>
                              <span className="fs-sm text-secondary">
                                แจ้งเจ้าหน้าที่หอพักที่เมนู หอพัก › คืนหอพัก
                              </span>
                            </div>
                          ) : (
                            <Button size="sm" variant="danger" onClick={() => openForm(row)}>
                              <i className="ri-logout-box-r-line"></i> จำหน่าย / จบการศึกษา
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>

        {leftovers.length > 0 && (
          <Card className="card-one mb-4 border-warning">
            <Card.Header>
              <Card.Title as="h6" className="mb-0">
                จำหน่ายแล้วแต่ยังค้างที่พักในหอ ({leftovers.length} คน)
              </Card.Title>
            </Card.Header>
            <Card.Body>
              <div className="fs-sm text-secondary mb-2">
                เตียงเหล่านี้ยังถูกนับว่ามีคนอยู่ ทำให้หอขึ้นว่าเต็มทั้งที่รับนักเรียนใหม่ได้ —
                กดย้ายออกเพื่อคืนเตียงให้ฝ่ายหอพัก
              </div>
              <div className="table-responsive">
                <Table className="table-bordered mb-0" hover>
                  <thead className="table-secondary">
                    <tr>
                      <th style={{ width: 120 }}>เลขประจำตัว</th>
                      <th>ชื่อ - สกุล</th>
                      <th style={{ width: 110 }}>ชั้นตอนออก</th>
                      <th style={{ width: 130 }}>ประเภท</th>
                      <th style={{ width: 110 }}>วันที่ออก</th>
                      <th style={{ width: 170 }}>หอพัก</th>
                      <th style={{ width: 150 }} className="text-center">จัดการ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leftovers.map((row) => (
                      <tr key={row.alloc_id}>
                        <td>{row.st_idnumber}</td>
                        <td>{row.st_name}</td>
                        <td>{row.ex_class || '-'}</td>
                        <td>
                          {row.ex_type
                            ? <Badge bg={row.ex_type === 'graduate' ? 'info' : 'secondary'}>{TYPE_LABEL[row.ex_type]}</Badge>
                            : <span className="fs-sm text-secondary">ไม่มีใบจำหน่าย</span>}
                        </td>
                        <td>{row.ex_date ? toThaiDate(row.ex_date) : '-'}</td>
                        <td className="fs-sm">
                          <div>{row.dorm_name}</div>
                          {row.village_name && <div className="text-secondary">{row.village_name}</div>}
                        </td>
                        <td className="text-center">
                          <Button size="sm" variant="outline-warning" onClick={() => handleReleaseDorm(row)}>
                            <i className="ri-home-gear-line"></i> ย้ายออกจากหอ
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        )}

        <StudentExitBackfill onDone={refreshAfterBackfill} />

        <Card className="card-one">
          <Card.Header>
            <Card.Title as="h6" className="mb-0">รายการล่าสุด</Card.Title>
          </Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <Table className="table-bordered mb-0" hover>
                <thead className="table-secondary">
                  <tr>
                    <th style={{ width: 120 }}>เลขประจำตัว</th>
                    <th>ชื่อ - สกุล</th>
                    <th style={{ width: 110 }}>ชั้นตอนออก</th>
                    <th style={{ width: 130 }}>ประเภท</th>
                    <th style={{ width: 110 }}>วันที่</th>
                    <th style={{ width: 150 }}>สาเหตุ</th>
                    <th style={{ width: 190 }}>ไปเรียนต่อที่</th>
                    <th style={{ width: 140 }}>ผู้บันทึก</th>
                    <th style={{ width: 80 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {recent.length === 0 && (
                    <tr><td colSpan="9" className="text-center text-secondary py-4">ยังไม่มีรายการ</td></tr>
                  )}
                  {recent.map((row) => (
                    <tr key={row.ex_id}>
                      <td>{row.st_idnumber}</td>
                      <td>
                        <div>{row.st_name}</div>
                        {row.dorm_name && (
                          <div className="fs-sm text-secondary">คืนที่พักหอ {row.dorm_name} แล้ว</div>
                        )}
                      </td>
                      <td>{row.ex_class || '-'}</td>
                      <td>
                        <Badge bg={row.ex_type === 'graduate' ? 'info' : 'secondary'}>{TYPE_LABEL[row.ex_type]}</Badge>
                      </td>
                      <td>{toThaiDate(row.ex_date)}</td>
                      <td className="fs-sm">{row.ex_reason || '-'}</td>
                      <td className="fs-sm">{row.ex_destination || '-'}</td>
                      <td className="fs-sm">{row.ex_by_name || '-'}</td>
                      <td className="text-center text-nowrap">
                        <Button size="sm" variant="link" className="p-0 me-2" title="แก้ไขข้อมูลการออก"
                          onClick={() => openEdit(row)}>
                          <i className="ri-edit-line"></i>
                        </Button>
                        <Button size="sm" variant="link" className="p-0" title="ยกเลิกการจำหน่าย"
                          onClick={() => handleCancelExit(row)}>
                          <i className="ri-arrow-go-back-line"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>

        <Footer />
      </div>

      <Modal show={showForm} onHide={() => setShowForm(false)} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title as="h6" className="mb-0">
            {MODE_TITLE[mode]} — {target?.st_name} ({target?.st_idnumber})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md="12">
              <Form.Label className="d-block">ประเภท</Form.Label>
              <Form.Check
                inline type="radio" id="exit-type-graduate" label="สำเร็จการศึกษา (จบ ม.3 / ม.6)"
                checked={form.ex_type === 'graduate'}
                onChange={() => setField('ex_type', 'graduate')}
              />
              <Form.Check
                inline type="radio" id="exit-type-withdraw" label="จำหน่าย (ออกก่อนจบหลักสูตร)"
                checked={isWithdraw}
                onChange={() => setField('ex_type', 'withdraw')}
              />
              <div className="fs-sm text-secondary mt-1">
                {editId
                  ? `ชั้นตอนออก ${target?.ex_class || '-'}${target?.ex_year ? ` (${target.ex_year})` : ''}`
                  : `ชั้นล่าสุด ${bothClassesLabel(target)}`}
              </div>
              {editId && (
                <div className="fs-sm text-secondary">
                  แก้ได้เฉพาะรายละเอียดการออก — สถานะนักเรียนและชั้นตอนออกไม่เปลี่ยน
                  {' '}ถ้ากดผิดคนให้ใช้ปุ่ม "ยกเลิกการจำหน่าย" แทน
                </div>
              )}
              {mode === 'backfill' && (
                <div className="fs-sm text-secondary">
                  นักเรียนคนนี้ออกจากทะเบียนไปแล้ว การใส่ข้อมูลย้อนหลังจึงไม่เปลี่ยนสถานะ
                  {' '}และไม่แตะทะเบียนหอพัก — เติมเฉพาะวันที่ สาเหตุ และปลายทางที่ยังว่างอยู่
                </div>
              )}
            </Col>

            <Col md="6">
              <Form.Label>วันที่{isWithdraw ? 'จำหน่าย' : 'สำเร็จการศึกษา'}</Form.Label>
              <InputGroup>
                <Form.Control
                  type="date"
                  value={form.ex_date}
                  onChange={(e) => setField('ex_date', e.target.value)}
                />
                <Button variant="outline-primary" onClick={() => setField('ex_date', todayISO())}>วันนี้</Button>
              </InputGroup>
              <div className="fs-sm text-secondary mt-1">
                {form.ex_date ? `ตรงกับ ${toThaiDate(form.ex_date)} (พ.ศ.)` : 'ยังไม่ได้เลือกวันที่'}
              </div>
            </Col>

            <Col md="6">
              <Form.Label>สาเหตุ{isWithdraw ? '' : ' (ไม่ต้องระบุ)'}</Form.Label>
              <Form.Select
                value={form.reasonChoice}
                disabled={!isWithdraw}
                onChange={(e) => setField('reasonChoice', e.target.value)}
              >
                {mode === 'backfill' && <option value="">ไม่ทราบ — ข้อมูลเดิมไม่ได้บันทึกไว้</option>}
                {WITHDRAW_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </Form.Select>
              {isWithdraw && form.reasonChoice === 'อื่นๆ' && (
                <Form.Control
                  className="mt-2"
                  maxLength={100}
                  placeholder="ระบุสาเหตุ"
                  value={form.reasonOther}
                  onChange={(e) => setField('reasonOther', e.target.value)}
                />
              )}
            </Col>

            <Col md="12">
              <Form.Label>ไปเรียนต่อที่ (สถานศึกษาใหม่)</Form.Label>
              <Form.Control
                maxLength={255}
                placeholder={isWithdraw ? 'เช่น โรงเรียนบ้านโคกยาง จ.ปัตตานี' : 'เช่น มหาวิทยาลัยสงขลานครินทร์ (ถ้าทราบ)'}
                value={form.ex_destination}
                onChange={(e) => setField('ex_destination', e.target.value)}
              />
              <div className="fs-sm text-secondary mt-1">
                {isWithdraw
                  ? 'ช่องนี้คือคำตอบของ "ออกไปแล้วไปไหน" ของนักเรียนที่ยังไม่จบ ม.3 / ม.6 — กรอกไว้เท่าที่ทราบ ยังไม่ทราบก็เว้นว่างได้'
                  : 'กรอกไว้เท่าที่ทราบ ไม่บังคับ'}
              </div>
            </Col>

            <Col md="12">
              <Form.Label>หมายเหตุ</Form.Label>
              <Form.Control
                maxLength={255}
                value={form.ex_note}
                onChange={(e) => setField('ex_note', e.target.value)}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowForm(false)} disabled={saving}>ปิด</Button>
          <Button variant={mode === 'create' ? 'danger' : 'primary'} onClick={handleSave} disabled={saving}>
            {saving ? 'กำลังบันทึก...' : MODE_SAVE[mode]}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
}
